import commonStyles from 'config/commonStyles';
import { truncateString } from 'helpers/helperFunctions';
import { images } from 'assets';
import { makeStyles, Theme, createStyles, alpha, Tooltip, Checkbox } from '@material-ui/core';
import { Autocomplete, AutocompleteRenderOptionState } from '@material-ui/lab';
import { TextField } from '@material-ui/core';
import React from 'react';
import clsx from 'clsx';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        ...commonStyles,
        listBox: {
            marginTop: 0,
            padding: 0,
        },
        paper: {
            marginTop: 0,
        },
        dropdownItem: {
            padding: 0,
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
            },
        },
        selectedDropdownItem: {
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
        },
        popper: {
            position: 'fixed',
        },
        textEllipsis: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            paddingRight: 10,
        }
    })
);

export interface MultiSelectAutocompleteProps<T> {
    options: T[];
    inputLabel: string;
    inputPlaceholder?: string;
    selectedValues?: T[];
    onChange?: (event: React.ChangeEvent<{}>, values: T[]) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    getOptionLabel: (option: T) => string;
    getOptionSelected?: (option: T, value: T) => boolean;
    style?: React.CSSProperties;
    popupIcon?: string;
    trimLength?: number;
    error?: React.ReactNode | boolean;
    name?: string;
    renderOption?: (option: T, state: AutocompleteRenderOptionState) => React.ReactNode;
    autoCompleteProps?: Partial<React.ComponentProps<typeof Autocomplete>>;
    textFieldProps?: Partial<React.ComponentProps<typeof TextField>>;
}

function MultiSelectAutocomplete<T>({
    options,
    inputLabel,
    inputPlaceholder,
    selectedValues = [],
    onChange,
    onFocus,
    onBlur,
    getOptionLabel,
    getOptionSelected,
    renderOption,
    style,
    popupIcon = images.icons.filter,
    trimLength = 25,
    error,
    name = 'multiselect',
    autoCompleteProps,
    textFieldProps,
}: MultiSelectAutocompleteProps<T>) {
    const classes = useStyles();

    function getSimpleText() {
        return selectedValues.map(getOptionLabel).join(' / ');
    }

    function getLabelText() {
        if (error) {
            return error;
        }
        if (selectedValues.length > 0) {
            return truncateString(getSimpleText(), trimLength);
        }
        return inputLabel;
    }

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            value={selectedValues}
            getOptionLabel={getOptionLabel as (option: unknown) => string}
            getOptionSelected={
                getOptionSelected as ((option: unknown, value: unknown) => boolean) | undefined
            }
            style={style}
            popupIcon={
                <img
                    src={popupIcon}
                    alt="filter-icon"
                    style={{
                        width: popupIcon === images.icons.filter ? 18 : 'auto',
                        marginRight: 5,
                    }}
                />
            }
            onChange={
                onChange as ((event: React.ChangeEvent<{}>, values: unknown) => void) | undefined
            }
            onFocus={onFocus}
            onBlur={onBlur}
            classes={{
                option: classes.dropdownItem,
                listbox: classes.listBox,
                paper: classes.paper,
                popper: popupIcon === images.icons.filter ? undefined : classes.popper,
            }}
            renderTags={() => null}
            renderOption={
                renderOption
                    ? (option, state) => renderOption(option as T, state)
                    : (option, { selected }: AutocompleteRenderOptionState) => (
                          <div
                              className={clsx(
                                  classes.flexRowSpaceBetween,
                                  classes.width100,
                                  classes.bottomLine,
                                  {
                                      [classes.selectedDropdownItem]: selected,
                                  }
                              )}
                          >
                              <div className={clsx(classes.flexRow, classes.width100)}>
                                  <Checkbox
                                      color="primary"
                                      checked={selected}
                                      style={{ marginRight: 8 }}
                                  />
                                  <div className={classes.textEllipsis}>
                                      {getOptionLabel(option as T)}
                                  </div>
                              </div>
                          </div>
                      )
            }
            renderInput={(params) => (
                <Tooltip title={getSimpleText()}>
                    <TextField
                        {...params}
                        {...textFieldProps}
                        name={name}
                        label={getLabelText()}
                        placeholder={inputPlaceholder}
                        variant="filled"
                        error={!!error}
                        onFocus={onFocus}
                        InputProps={{
                            ...params.InputProps,
                            ...textFieldProps?.InputProps,
                            style: {
                                opacity: popupIcon === images.icons.filter ? 0.6 : 1,
                                cursor: 'pointer',
                                ...textFieldProps?.InputProps?.style,
                            },
                        }}
                    />
                </Tooltip>
            )}
            {...autoCompleteProps}
        />
    );
}

export default MultiSelectAutocomplete;
